import { useState, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';

interface Props {
  onLogin: () => void;
}

const AdminLogin = ({ onLogin }: Props) => {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: dbErr } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'admin_password')
        .maybeSingle();
      if (dbErr || !data) { setError('Error al verificar. Inténtalo de nuevo.'); return; }
      const stored = typeof data.value === 'string' ? data.value : JSON.stringify(data.value);
      const clean = stored.replace(/^"|"$/g, '');
      if (clean === password) { onLogin(); }
      else { setError('Contraseña incorrecta. Inténtalo de nuevo.'); }
    } catch { setError('Error de conexión.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-400/4 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 text-black mb-5">
            <i className="ri-shield-keyhole-line text-3xl" />
          </div>
          <h1 className="font-display text-4xl uppercase text-white tracking-wider">Panel Admin</h1>
          <p className="text-zinc-500 text-sm mt-1.5 font-light">Taller Mecánico Kevin Alberto</p>
        </div>

        {/* Form card */}
        <div className="bg-zinc-900 border border-zinc-800 p-7">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">
                Contraseña de acceso
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  placeholder="••••••••"
                  className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-3 pr-11 outline-none focus:border-yellow-400/60 transition-colors placeholder:text-zinc-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white cursor-pointer w-6 h-6 flex items-center justify-center transition-colors"
                >
                  <i className={`${showPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/20 px-4 py-3">
                <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0" />
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-yellow-300 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin" /> Verificando...</>
              ) : (
                <><i className="ri-login-box-line" /> Entrar al panel</>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-zinc-600 text-xs mt-5 font-light">
          Acceso restringido — solo personal autorizado
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
